import PageShell from "../components/PageShell";
import company from "../data/company.json";

/**
 * /cookies — Cookies Information page.
 *
 * Purely informational: lists every cookie the storefront may set, what
 * it's for, how long it lives, and how to remove it. There is no consent
 * banner or opt-in toggle — Loadout does not run advertising trackers.
 */

const COOKIES = [
  {
    name: "cart_session_id",
    party: "First-party",
    purpose:
      "Persists the guest cart across pages so items remain in your bag until checkout.",
    duration: "14 days",
    category: "Strictly necessary",
  },
  {
    name: "oauth_state",
    party: "First-party",
    purpose:
      "Signed one-time value used during Shopify Customer Account sign-in to prevent CSRF and replay attacks. Cleared as soon as sign-in finishes.",
    duration: "5 minutes",
    category: "Strictly necessary",
  },
  {
    name: "oat_id / oat_at / oat_rt",
    party: "First-party",
    purpose:
      "Signed, HttpOnly session cookies that keep you signed in to your Loadout account after a successful Shopify Customer Account login. Never accessible to JavaScript.",
    duration: "Up to 30 days (refresh token); 1 hour (access + id tokens)",
    category: "Strictly necessary",
  },
  {
    name: "loadout_visited",
    party: "First-party",
    purpose:
      "Marks a returning visitor so the homepage hero switches from the full 100vh reveal to the compact 75vh state on repeat visits.",
    duration: "30 days",
    category: "Preferences",
  },
  {
    name: "loadout-theme-pref",
    party: "First-party (localStorage)",
    purpose:
      "By default (Auto) the site follows your device's light/dark setting. Saved only when you pick Light or Dark from the theme menu, so your choice applies instantly next visit; picking Auto removes it.",
    duration: "Until you clear site storage",
    category: "Preferences",
  },
  {
    name: "shopify_*",
    party: "Third-party (Shopify)",
    purpose:
      "Set by Shopify when you reach the hosted Customer Account or checkout flow. Loadout does not read or control these cookies.",
    duration: "Session to 30 days (Shopify-managed)",
    category: "Strictly necessary",
  },
  {
    name: "razorpay_*",
    party: "Third-party (Razorpay)",
    purpose:
      "Set by the Razorpay payment window when you place an order. Handles the payment session between your device and Razorpay's servers.",
    duration: "Session (Razorpay-managed)",
    category: "Strictly necessary",
  },
  {
    name: "__cf_bm",
    party: "Third-party (Cloudflare)",
    purpose:
      "Cloudflare bot-management cookie set on our infrastructure to distinguish humans from automated traffic. No profiling, no ad targeting.",
    duration: "30 minutes rolling",
    category: "Strictly necessary",
  },
];

const CATEGORY_STYLES = {
  "Strictly necessary": "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40",
  Preferences: "bg-white/10 text-white/80 border-white/25",
};

export default function CookiesPage() {
  return (
    <PageShell title="Cookies Information — Loadout" path="/cookies">
      <p
        data-testid="cookies-eyebrow"
        className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]"
      >
        Loadout / Privacy
      </p>
      <h1
        data-testid="cookies-heading"
        className="mt-3 font-display uppercase leading-[0.95] text-[38px] md:text-[48px] lg:text-[64px]"
      >
        <span className="text-white">Cookies </span>
        <span className="text-[#F59E0B]">information</span>
      </h1>
      <p className="mt-6 font-mono text-[11px] tracking-[0.14em] uppercase text-white/60">
        Last updated · 2026-02
      </p>

      <div className="mt-10 max-w-[76ch] space-y-6 text-white/80 leading-relaxed">
        <p>
          Loadout uses a small number of cookies and equivalent browser
          storage to keep your cart intact, keep you signed in, remember
          your theme choice, and process payments. We do not run
          third-party advertising trackers or cross-site profiling, so
          there is no consent banner or opt-in toggle — only the
          strictly-necessary and preference cookies listed below.
        </p>
        <p>
          You can remove any of these at any time by clearing site data
          for <span className="font-mono text-[13px] text-white">loadout.co.in</span>{" "}
          in your browser settings. Clearing them will sign you out and
          empty your cart.
        </p>
      </div>

      {/* Cookie table — desktop */}
      <div
        data-testid="cookies-table-desktop"
        className="mt-10 hidden md:block overflow-x-auto border border-white/10"
      >
        <table className="w-full text-sm border-collapse">
          <thead className="bg-white/[0.03]">
            <tr className="text-left text-white/50 uppercase font-mono text-[11px] tracking-[0.14em]">
              <th className="p-3 border-b border-white/10">Cookie</th>
              <th className="p-3 border-b border-white/10">Party</th>
              <th className="p-3 border-b border-white/10">Purpose</th>
              <th className="p-3 border-b border-white/10">Duration</th>
              <th className="p-3 border-b border-white/10">Category</th>
            </tr>
          </thead>
          <tbody className="text-white/75">
            {COOKIES.map((c) => (
              <tr key={c.name} className="border-b border-white/5 align-top">
                <td className="p-3 font-mono text-[12px] text-white whitespace-nowrap">
                  {c.name}
                </td>
                <td className="p-3 whitespace-nowrap text-white/60 text-[13px]">
                  {c.party}
                </td>
                <td className="p-3 text-[13px]">{c.purpose}</td>
                <td className="p-3 whitespace-nowrap text-white/60 text-[13px]">
                  {c.duration}
                </td>
                <td className="p-3 whitespace-nowrap">
                  <span
                    className={`inline-block border px-2 py-1 font-mono text-[10px] tracking-[0.14em] uppercase ${
                      CATEGORY_STYLES[c.category] || "border-white/20 text-white/60"
                    }`}
                  >
                    {c.category}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cookie cards — mobile */}
      <div
        data-testid="cookies-list-mobile"
        className="mt-10 md:hidden space-y-4"
      >
        {COOKIES.map((c) => (
          <div
            key={c.name}
            className="border border-white/10 p-4 bg-white/[0.02]"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[13px] text-white break-all">
                {c.name}
              </span>
              <span
                className={`shrink-0 border px-2 py-1 font-mono text-[9px] tracking-[0.14em] uppercase ${
                  CATEGORY_STYLES[c.category] || "border-white/20 text-white/60"
                }`}
              >
                {c.category}
              </span>
            </div>
            <p className="mt-3 text-[13px] text-white/75 leading-relaxed">
              {c.purpose}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 font-mono text-[10px] tracking-[0.14em] uppercase text-white/50">
              <div>
                <div className="text-white/35">Party</div>
                <div className="mt-1 text-white/70 normal-case tracking-normal text-[12px] font-sans">
                  {c.party}
                </div>
              </div>
              <div>
                <div className="text-white/35">Duration</div>
                <div className="mt-1 text-white/70 normal-case tracking-normal text-[12px] font-sans">
                  {c.duration}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="mt-12 max-w-[76ch] space-y-4 text-white/80 leading-relaxed">
        <h2 className="text-[#F59E0B] font-mono text-[13px] tracking-[0.16em] uppercase">
          § How to remove cookies
        </h2>
        <p>
          Every modern browser lets you clear site cookies from its privacy
          settings. On iOS Safari: Settings → Safari → Advanced → Website
          Data → search "loadout" → Delete. On Chrome desktop: DevTools →
          Application → Storage → Clear site data.
        </p>
        <h2 className="text-[#F59E0B] font-mono text-[13px] tracking-[0.16em] uppercase">
          § Questions
        </h2>
        <p>
          Write to{" "}
          <a
            href={`mailto:${company.support_email}`}
            data-testid="cookies-contact-email"
            className="text-[#F59E0B] hover:underline"
          >
            {company.support_email}
          </a>{" "}
          and we'll respond within one business day.
        </p>
      </section>
    </PageShell>
  );
}
